import type { StufenBeschreibung } from '@luzula/luzula-lib';
import { FESTIGKEIT_STUFEN, FestigkeitStufe } from './festigkeit-stufen';

/**
 * Die drei Lagerungsdichte-Stufen und ihre Ansprache im Gelände.
 *
 * Quelle der Zuordnung: „Leistungsbeschreibung zur Erhebung von Standortsdaten
 * in M-V" (Anlage 2), Abschnitt 3.3.2 „Lagerungsdichte" — Landesforst
 * Mecklenburg-Vorpommern. Wörtlich: „Die Einstufung der Lagerungsdichte erfolgt
 * in drei Stufen: 1 = gering, 2 = mittel, 3 = hoch. Die Zahlen beziehen sich auf
 * die Definition in der SEA Teil B, S. 76 – Festigkeit. Dabei bedeuten:
 * 1 = f0 und f1, 2 = f2, 3 = f3 und f4."
 *
 * SEA 95 selbst führt unter dem Namen „Lagerungsdichte" nichts; sie liefert nur
 * die Festigkeitsstufen (siehe [festigkeit-stufen.ts]).
 */
export interface LagerungsdichteStufe {
	wert: 1 | 2 | 3;
	name: string;
	/** Festigkeitsstufen nach SEA, die zu dieser Stufe gehören. */
	festigkeit: string[];
}

export const LAGERUNGSDICHTE_STUFEN: LagerungsdichteStufe[] = [
	{ wert: 1, name: 'gering', festigkeit: ['f0', 'f1'] },
	{ wert: 2, name: 'mittel', festigkeit: ['f2'] },
	{ wert: 3, name: 'hoch', festigkeit: ['f3', 'f4'] },
];

/**
 * Drei zarte Töne (grünlich, gelblich, rötlich) je Stufe — nur zur Orientierung.
 * Färben das Hilfefenster (Legende) UND das Feld in der Substrattabelle.
 */
export const LAGERUNGSDICHTE_FARBEN: Readonly<Record<1 | 2 | 3, string>> = {
	1: '#eef6ee',
	2: '#fdf6e3',
	3: '#f6eeee',
};

/** Die Stufen für das Stufen-Feld (`wd-stufen-input`): Wert, Farbe, Name. */
export const LAGERUNGSDICHTE_STUFEN_FELD: StufenBeschreibung[] = LAGERUNGSDICHTE_STUFEN.map(s => ({
	wert: s.wert, farbe: LAGERUNGSDICHTE_FARBEN[s.wert], name: s.name,
}));

/** Die Festigkeitsstufen einer Dichtestufe, in der Reihenfolge der Anweisung. */
export function festigkeitenZurStufe(stufe: LagerungsdichteStufe): FestigkeitStufe[] {
	return stufe.festigkeit.map(code => {
		const treffer = FESTIGKEIT_STUFEN.find(f => f.code === code);
		if (!treffer) throw new Error(`Festigkeitsstufe ${code} gibt es nicht`);
		return treffer;
	});
}
